/**
 * Walk-in minister at the desk (not pre-registered online): the same data the online form collects,
 * prefilled from the minister's record. District, region, grado and cargo come from the record and
 * are read-only; contact data can be corrected (kept as a snapshot for the administration to merge).
 * Also takes the CI photos, an optional signature and the declaration (see DeskEvidenceFields).
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { formatPrice } from '@/models/event';
import { uploadRegistrationDocument } from '../../public/publicRegistrationRepository';
import { registerMinisterAtDesk } from '../deskRepository';
import { missingEvidence, type DeskContact, type DeskEvidence, type DeskPerson, type DeskPricing } from '../deskLogic';
import { DeskEvidenceFields } from './DeskEvidenceFields';

export interface DeskEventInfo {
  registration_form_type: string | null;
  registration_declaration: string | null;
  /** DIGITAL = the drawn signature is mandatory */
  signature_mode?: string | null;
}

interface DeskMinisterDialogProps {
  eventId: string;
  event: DeskEventInfo;
  person: DeskPerson;
  pricing: DeskPricing;
  contact: DeskContact;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRegistered: () => void;
}

function ReadOnly({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <p className="min-h-9 rounded-md border bg-muted/40 px-3 py-2 text-sm">{value || '—'}</p>
    </div>
  );
}

export function DeskMinisterDialog({
  eventId,
  event,
  person,
  pricing,
  contact,
  open,
  onOpenChange,
  onRegistered,
}: DeskMinisterDialogProps) {
  const { toast } = useToast();
  const full = event.registration_form_type === 'FULL_CONVENTION';
  const [phone, setPhone] = useState(contact.cell_phone ?? '');
  const [officePhone, setOfficePhone] = useState(contact.office_phone ?? '');
  const [email, setEmail] = useState(contact.email ?? '');
  const [address, setAddress] = useState(contact.address ?? '');
  const [birthDate, setBirthDate] = useState(pricing.birth_date ?? '');
  const [evidence, setEvidence] = useState<DeskEvidence>({ declaration: false, signature: null });
  const [busy, setBusy] = useState(false);

  const signatureRequired = event.signature_mode === 'DIGITAL';
  const missing = missingEvidence('MINISTER', evidence, signatureRequired);
  const canSubmit = phone.trim() !== '' && missing.length === 0;

  const submit = async () => {
    if (!canSubmit || !person.minister_id) return;
    setBusy(true);
    try {
      const documents: Record<string, string> = {
        ci_front: evidence.ci_front as string,
        ci_back: evidence.ci_back as string,
      };
      if (evidence.signature) {
        documents.signature = await uploadRegistrationDocument(eventId, 'signature', evidence.signature);
      }

      const participant = await registerMinisterAtDesk(eventId, person.minister_id, pricing, {
        registration_data: {
          kind: 'MINISTER',
          full_name: person.full_name,
          dni: person.dni,
          // From the minister's record (not editable here)
          district_id: pricing.district_id,
          region_id: pricing.region_id,
          degree_id: pricing.degree_id,
          position: pricing.position,
          // Contact data confirmed with the person
          cell_phone: phone.trim(),
          email: email.trim() || undefined,
          birth_date: birthDate || undefined,
          ...(full ? { office_phone: officePhone.trim() || undefined, address: address.trim() || undefined } : {}),
        },
        birth_date: birthDate || null,
        documents,
        declaration_accepted: evidence.declaration,
        signed: !!evidence.signature,
      });
      toast({ title: 'Inscrito', description: `${person.full_name} · ${formatPrice(Number(participant.calculated_price ?? 0))}` });
      onOpenChange(false);
      onRegistered();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'No se pudo inscribir', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Inscribir a {person.full_name}</DialogTitle>
          <DialogDescription>
            Confirme los datos con la persona, tome las fotos de la CI y, si quiere, pásele la tablet para que firme.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <ReadOnly label="CI" value={person.dni} />
            <ReadOnly label="Registro ADB" value={person.adb_code} />
            <ReadOnly label="Distrito" value={person.district_name} />
            <ReadOnly label="Región" value={person.region_name} />
            {full && <ReadOnly label="Grado ministerial" value={person.degree_name} />}
            {full && <ReadOnly label="Cargo" value={pricing.position} />}
          </div>
          <p className="text-xs text-muted-foreground">
            Distrito, región, grado y cargo vienen del registro ministerial y no se editan aquí.
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="walkin-phone">Celular *</Label>
              <Input id="walkin-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="walkin-birth">Fecha de nacimiento</Label>
              <Input id="walkin-birth" type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="walkin-email">E-mail</Label>
              <Input id="walkin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Opcional" />
            </div>
            {full && (
              <div className="space-y-1.5">
                <Label htmlFor="walkin-office">Teléfono de oficina</Label>
                <Input id="walkin-office" type="tel" value={officePhone} onChange={(e) => setOfficePhone(e.target.value)} />
              </div>
            )}
            {full && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="walkin-address">Dirección para envío de correspondencia</Label>
                <Input id="walkin-address" value={address} onChange={(e) => setAddress(e.target.value)} />
              </div>
            )}
          </div>

          <DeskEvidenceFields
            eventId={eventId}
            kind="MINISTER"
            value={evidence}
            onChange={setEvidence}
            declarationText={event.registration_declaration}
            signatureRequired={signatureRequired}
            disabled={busy}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button size="lg" onClick={submit} disabled={busy || !canSubmit}>
            {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
            Inscribir en el evento
          </Button>
        </DialogFooter>
        {phone.trim() === '' && <p className="text-xs text-muted-foreground">El celular es obligatorio.</p>}
      </DialogContent>
    </Dialog>
  );
}
